<template>
  <section
    aria-roledescription="carousel"
    aria-label="Listagem de Cartões"
    ref="rootRef"
    class="ca-card-fan-carousel"
    :class="{ 'is-in-view': inView }"
  >
    <div class="ca-bullet-container">
      <!-- Mobile: native scroll-snap track (finger drives the scroll, the
           browser snaps to the centred card and JS reads the position to drive
           the dots/message). -->
      <div class="ca-card-fan-carousel__mobile flex flex-col items-center">
        <div class="ca-card-fan-carousel__inner flex flex-col items-center">
          <ul
            ref="stageRef"
            class="ca-card-fan-carousel__stage"
            :class="{
              'ca-card-fan-carousel__stage--jumping': suppressTransition,
            }"
            role="region"
            aria-live="polite"
            tabindex="0"
            @scroll.passive="onScroll"
            @scrollend="onScrollEnd"
            @keydown="onKeydown"
          >
            <li
              v-for="(entry, i) in renderedCards"
              :key="i"
              class="ca-card-fan-carousel__card"
              :class="{
                'ca-card-fan-carousel__card--active': i === centeredRendered,
              }"
              :style="mobileCardStyle(i)"
              :aria-hidden="
                entry.realIndex !== activeIndex ? 'true' : undefined
              "
              :aria-label="cardLabel(entry.card, entry.realIndex)"
              :aria-current="i === centeredRendered ? 'true' : undefined"
            >
              <div class="ca-card-fan-carousel__card-inner">
                <BaseCard
                  class="ca-card-fan-carousel__media"
                  :image="entry.card.imageUrl"
                  :image-alt="entry.card.imageAlt"
                  :loading="isLoading"
                />
              </div>
            </li>
          </ul>

          <CaPageControl
            v-if="cards.length > 1"
            class="ca-card-fan-carousel__dots"
            :count="cards.length"
            :model-value="activeIndex"
            @update:model-value="goTo"
          />
        </div>

        <div class="pt-size-8 gap-size-7 flex flex-col items-center">
          <CaModuleMessage
            v-if="displayedCard"
            class="ca-card-fan-carousel__message ca-card-fan-carousel__message--mobile"
            :class="{ 'is-leaving': msgLeaving }"
            :label="displayedCard.label"
            :title="displayedCard.title"
            :description="displayedCard.description"
            :primaryLabel="displayedCard.ctaText"
            theme="default"
            @primary-click="onCtaClick(displayedCard)"
          />
        </div>
      </div>

      <!-- Desktop: static fan on the left, navigation + message on the right.
           Each card fans out from a shared bottom pivot at a fixed angle; the
           selected card scales up and moves to the front while the arrows step
           the selection one card at a time. -->
      <div class="ca-card-fan-carousel__desktop">
        <div class="ca-card-fan-carousel__fan-col">
          <ul
            ref="fanRef"
            class="ca-card-fan-carousel__fan"
            :class="{
              'ca-card-fan-carousel__fan--visible': inView,
              'ca-card-fan-carousel__fan--animate': animateFan,
            }"
            :style="fanBoxStyle"
            role="region"
            aria-roledescription="carrossel"
            aria-label="Cartões em destaque"
            aria-live="polite"
          >
            <li
              ref="fanAnchorRef"
              class="ca-card-fan-carousel__fan-anchor"
              aria-hidden="true"
            ></li>
            <li
              v-for="(card, i) in fanCards"
              :key="i"
              class="ca-card-fan-carousel__fan-card"
              :class="{
                'ca-card-fan-carousel__fan-card--active':
                  !isLoading && i === activeIndex,
              }"
              :style="fanStyle(i)"
              role="button"
              tabindex="0"
              :aria-label="cardLabel(card, i)"
              :aria-current="i === activeIndex ? 'true' : undefined"
              @click="goToReal(i)"
              @keydown.enter.prevent="goToReal(i)"
              @keydown.space.prevent="goToReal(i)"
            >
              <!-- The rotation lives on the outer element (fan pivot); the scale
                 lives here so the selected card grows about its own centre
                 without shifting position. -->
              <div class="ca-card-fan-carousel__fan-card-inner">
                <BaseCard
                  class="ca-card-fan-carousel__media"
                  :image="card.imageUrl"
                  :image-alt="card.imageAlt"
                  :loading="isLoading"
                />
              </div>
            </li>
          </ul>
        </div>

        <div class="ca-card-fan-carousel__panel flex flex-col">
          <div
            v-if="cards.length > 1"
            class="ca-card-fan-carousel__arrows flex"
          >
            <IconButton
              size="s"
              type="theme"
              variant="secondary"
              aria-label="Cartão anterior"
              @clicked="prev"
            >
              <template #trailingIcon>
                <IconComponent size="2" name="chev_left" />
              </template>
            </IconButton>
            <IconButton
              size="s"
              type="theme"
              variant="secondary"
              aria-label="Cartão seguinte"
              @clicked="next"
            >
              <template #trailingIcon>
                <IconComponent size="2" name="chev_right" />
              </template>
            </IconButton>
          </div>

          <Transition name="ca-card-fan-carousel-fade" mode="out-in">
            <div
              v-if="activeCard"
              :key="activeIndex"
              class="ca-card-fan-carousel__panel-content gap-size-7 flex flex-col"
            >
              <CaModuleMessage
                class="ca-card-fan-carousel__message"
                :label="activeCard.label"
                :title="activeCard.title"
                :description="activeCard.description"
                :primaryLabel="activeCard.ctaText"
                theme="default"
                @primary-click="onCtaClick(activeCard)"
              />
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const isDev = import.meta.env.DEV;
</script>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import CaPageControl from "../../base/CaPageControl.vue";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";
import BaseCard from "../../base/BaseCard.vue";
import { BaseButton, IconButton, IconComponent } from "ca-online-design-system";

defineOptions({
  name: "CaCardFanCarousel",
});

// Desktop fan angles (degrees) per card count, from the design spec
// ("Leque N - Ângulo"). The component supports 5 to 9 cards; each entry lists
// one fixed angle per card, in order.
// Spec fan angles (degrees) per card count ("Leque N - Ângulo"). The component
// supports 5 to 9 cards; each entry lists one fixed angle per card, in order.
const FAN_ANGLES_BY_COUNT = {
  5: [-15, 15, 45, 75, 105],
  6: [-15, 15, 50, 80, 115, 145],
  7: [-15, 15, 40, 65, 95, 120, 145],
  8: [-15, 10, 30, 55, 75, 100, 125, 145],
  9: [-15, 5, 25, 45, 65, 85, 105, 125, 145],
};

// Measured shape of the fan from the 9-card design: for every card rotation
// (−angle) the card's offset from the front card, in units of the card width.
// Any card count reads its own spec angles above and samples each card's
// position off this one curve, so all fans share the same visual arc. Points
// are ordered by descending rotation (front card first).
const FAN_SHAPE = [
  { rot: 15, ox: 0.0, oy: -0.1 },
  { rot: -5, ox: -0.4, oy: 0.12 },
  { rot: -25, ox: -0.8, oy: 0.22 },
  { rot: -45, ox: -1.14, oy: 0.4 },
  { rot: -65, ox: -1.5, oy: 0.62 },
  { rot: -85, ox: -1.78, oy: 0.82 },
  { rot: -105, ox: -1.95, oy: 1.04 },
  { rot: -125, ox: -2.0, oy: 1.26 },
  { rot: -145, ox: -1.95, oy: 1.44 },
];

// The 5-card fan is tighter than the shared curve: its cards overlap more and
// sit closer to the front card. These per-card offsets (from the front card, in
// card-width units) are measured from the 5-card design and used instead of the
// shared curve for this count only; 7–9 keep the curve.
const FAN_OFFSETS_5 = [
  { ox: 0.0, oy: 0.0 },
  { ox: -0.56, oy: 0.06 },
  { ox: -0.97, oy: 0.21 },
  { ox: -1.25, oy: 0.55 },
  { ox: -1.35, oy: 1.08 },
];

// Same idea for the 6-card design: measured per-card offsets that sit tighter
// than the shared curve.
const FAN_OFFSETS_6 = [
  { ox: 0.0, oy: 0.0 },
  { ox: -0.56, oy: 0.06 },
  { ox: -1.02, oy: 0.27 },
  { ox: -1.3, oy: 0.5 },
  { ox: -1.42, oy: 0.92 },
  { ox: -1.35, oy: 1.2 },
];

// Same idea for the 7-card design.
const FAN_OFFSETS_7 = [
  { ox: 0.0, oy: 0.0 },
  { ox: -0.56, oy: 0.06 },
  { ox: -0.9, oy: 0.2 },
  { ox: -1.16, oy: 0.42 },
  { ox: -1.3, oy: 0.56 },
  { ox: -1.4, oy: 0.74 },
  { ox: -1.32, oy: 0.92 },
];

// Card counts with a dedicated (measured) offset table instead of the curve.
const FAN_OFFSETS_BY_COUNT = {
  5: FAN_OFFSETS_5,
  6: FAN_OFFSETS_6,
  7: FAN_OFFSETS_7,
};

// Interpolate a card's offset for a given rotation off the measured fan curve.
function offsetForRot(rot) {
  const pts = FAN_SHAPE;
  if (rot >= pts[0].rot) return { ox: pts[0].ox, oy: pts[0].oy };
  const end = pts[pts.length - 1];
  if (rot <= end.rot) return { ox: end.ox, oy: end.oy };
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (rot <= a.rot && rot >= b.rot) {
      const t = (rot - a.rot) / (b.rot - a.rot);
      return { ox: a.ox + (b.ox - a.ox) * t, oy: a.oy + (b.oy - a.oy) * t };
    }
  }
  return { ox: end.ox, oy: end.oy };
}

const props = defineProps({
  /** Cards shown in the fan; each card also carries the message below it. */
  cards: {
    type: Array,
    default: () => [],
  },
});

// Dev-only fallback used when the backend sends no data.
const mock = ref(null);
const cards = computed(() =>
  props.cards.length ? props.cards : (mock.value?.cards ?? []),
);

// Number of shimmer placeholder cards shown while the data is loading; matches
// the smallest supported fan so the desktop angles line up during loading.
const SKELETON_COUNT = 5;
// Loading: no card data has arrived yet. Mirrors CaModuleMessage, which already
// shows its own shimmer while the title is missing.
const isLoading = computed(() => !cards.value.length);
// Placeholder cards fed into the render paths so both layouts keep their shape
// (fan on desktop, scroll track on mobile) while shimmering.
const skeletonCards = computed(() =>
  Array.from({ length: SKELETON_COUNT }, () => ({ loading: true })),
);
// Desktop fan source: real cards, or skeletons while loading.
const fanCards = computed(() =>
  isLoading.value ? skeletonCards.value : cards.value,
);

// Real index of the centred card (drives dots + message).
const activeIndex = ref(0);
// Rendered index (inside the tripled loop) of the physically centred card;
// drives which card is scaled up.
const centeredRendered = ref(0);
const stageRef = ref(null);
// While the loop silently jumps between clone copies, the scale transition is
// disabled so the class moving to another copy doesn't animate a second time.
const suppressTransition = ref(false);

// Measured height of the fanned cards; the fan box is sized to wrap them so the
// desktop row grows with the card content (the cards are absolutely positioned,
// so their rotated extent is not otherwise reflected in layout).
const fanRef = ref(null);
const fanAnchorRef = ref(null);
const fanHeight = ref(null);
const fanCardBottom = ref(0);
// Horizontal shift that re-centres the fanned cards inside the fan column (the
// cards open out past the narrow box, so without this they hug the left edge).
const fanOffsetX = ref(0);
const fanBoxStyle = computed(() => ({
  ...(fanHeight.value ? { height: `${fanHeight.value}px` } : {}),
  "--fan-card-bottom": `${fanCardBottom.value}px`,
  "--fan-offset-x": `${fanOffsetX.value}px`,
}));

// Entrance animation: the fan "opens" (staggered, first → last card) and the
// message fades in once the component scrolls into view.
const rootRef = ref(null);
const inView = ref(false);
const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const animateFan = computed(() => inView.value && !prefersReducedMotion);

const activeCard = computed(() => cards.value[activeIndex.value] ?? null);

// Mobile message swap: keyed on a "settled" index that only changes once the
// swipe finishes, so the fade transition doesn't thrash (and stall) while the
// finger is still scrolling through cards.
const settledIndex = ref(0);
const settledCard = computed(() => cards.value[settledIndex.value] ?? null);

// Mobile message out-in swap driven by JS (not <Transition>): a plain CSS
// opacity transition fades the current message out, a timer swaps the content
// at the mid-point, then it fades back in. The timer always fires, so the
// message reliably reappears even through fast consecutive swipes — unlike
// <Transition mode="out-in">, whose leave can stall mid-swipe and never enter.
const MSG_SWAP_MS = 350;
const displayedCard = ref(null);
const msgLeaving = ref(false);
let msgSwapTimer = null;
watch(
  settledCard,
  (card) => {
    if (displayedCard.value === null || prefersReducedMotion) {
      displayedCard.value = card;
      return;
    }
    msgLeaving.value = true;
    clearTimeout(msgSwapTimer);
    msgSwapTimer = setTimeout(() => {
      displayedCard.value = card;
      msgLeaving.value = false;
    }, MSG_SWAP_MS);
  },
  { immediate: true },
);

// Desktop fan layout for the current card count (clamped to the supported
// 5–9 range): each card uses its own spec angle and samples its position off
// the shared fan shape, so every count is the same fan with the first and last
// cards fixed and only the middle redistributed.
const fanLayout = computed(() => {
  const count = Math.min(9, Math.max(5, cards.value.length));
  const angles = FAN_ANGLES_BY_COUNT[count];
  // Counts with a measured, tighter offset table use it; the rest sample the
  // shared curve so they keep the same arc.
  const offsets = FAN_OFFSETS_BY_COUNT[count];
  if (offsets) {
    return angles.map((angle, i) => ({
      ox: offsets[i].ox,
      oy: offsets[i].oy,
      rot: -angle,
      scale: 1,
    }));
  }
  return angles.map((angle) => {
    const rot = -angle;
    return { ...offsetForRot(rot), rot, scale: 1 };
  });
});

// Loop infinitely by rendering the cards three times and parking the user in
// the middle copy; each entry keeps its real index so dots/labels stay correct.
const renderedCards = computed(() => {
  if (isLoading.value) {
    return skeletonCards.value.map((card, i) => ({ card, realIndex: i }));
  }
  const list = cards.value;
  if (list.length > 1) {
    return [...list, ...list, ...list].map((card, i) => ({
      card,
      realIndex: i % list.length,
    }));
  }
  return list.map((card, i) => ({ card, realIndex: i }));
});

function cardLabel(card, index) {
  const title = card?.title ? `${card.title}. ` : "";
  return `${title}Cartão ${index + 1} de ${cards.value.length}`;
}

// Desktop fan: every card keeps its own measured placement (offset from the
// front card, rotation and scale) and stacking position; selecting a card never
// reorders the fan — it only scales the chosen card up in place (the extra scale
// lives on the inner wrapper). Offsets are multiplied by the card width in CSS
// so the fan stays correct across breakpoints. Index 0 is the front card.
function fanStyle(index) {
  const total = cards.value.length;
  const layout = fanLayout.value;
  const l = layout[Math.min(index, layout.length - 1)];
  return {
    "--card-ox": String(l.ox),
    "--card-oy": String(l.oy),
    "--card-rot": `${l.rot}deg`,
    "--card-scale": String(l.scale),
    "--card-delay": `${index * 60}ms`,
    zIndex: String(total - index),
  };
}

// Mobile fan-open entrance: each card starts collapsed exactly at the centre
// (hidden behind the opaque active card) and slides out to its own place; the
// active card opens first, its neighbours follow, staggered by distance. The
// per-card offset/delay are exposed as CSS vars so the slide is driven in CSS
// (the offset is multiplied by the real card width for a pixel-perfect collapse
// that also stays correct across breakpoints).
function mobileCardStyle(i) {
  // While loading, the skeletons sit open in the track (no collapse/stagger).
  if (isLoading.value) {
    return { "--enter-offset": "0", "--enter-delay": "0s" };
  }
  const offset = i - centeredRendered.value;
  const dist = Math.abs(offset);
  // Only the cards near the centre take part in the open; distant clones stay
  // off-screen so their stacked drop-shadows don't pile into a grey blob.
  const collapse = dist <= 2 ? offset : 0;
  const delay = offset === 0 ? 0 : 0.1 + Math.min(dist, 3) * 0.08;
  // Gap compensation: the scaled-down neighbours sit inset inside their box,
  // so the gap next to the full-size active card is smaller than the gap
  // between two neighbours. Pull each card toward the centre by (dist - 1) of
  // that inset so every gap matches the tight active↔neighbour one.
  const gapSteps = offset === 0 ? 0 : -Math.sign(offset) * (dist - 1);
  return {
    "--enter-offset": String(collapse),
    "--enter-delay": `${delay}s`,
    "--gap-steps": String(gapSteps),
  };
}

function onCtaClick(card) {
  const url = card?.ctaUrl;
  if (!url) return;
  if (card.ctaTarget === "_blank") {
    window.open(url, "_blank", "noopener");
  } else {
    window.location.href = url;
  }
}

// --- Native scroll-snap swipe with an infinite loop (3x clone set) ---
// Same mechanism as CaOverlappingCardsCarousel on mobile.
function getCardEls() {
  return Array.from(
    stageRef.value?.querySelectorAll(".ca-card-fan-carousel__card") || [],
  );
}

// Offset of the middle (real) copy inside the tripled render.
function loopOffset() {
  const total = cards.value.length;
  return total > 1 ? total : 0;
}

function nearestRenderedIndex() {
  const el = stageRef.value;
  if (!el) return 0;
  const centre = el.scrollLeft + el.clientWidth / 2;
  let nearest = 0;
  let best = Infinity;
  getCardEls().forEach((card, i) => {
    const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - centre);
    if (distance < best) {
      best = distance;
      nearest = i;
    }
  });
  return nearest;
}

function scrollToRendered(renderedIndex, behavior = "smooth") {
  const el = stageRef.value;
  const card = getCardEls()[renderedIndex];
  if (!el || !card) return;
  el.scrollTo({
    left: card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2,
    behavior,
  });
}

// The finger drives the scroll; sync the active card (scale + dots + aria).
// The debounce recenters even on browsers without the `scrollend` event.
let scrollIdleTimer = null;
function onScroll() {
  const total = cards.value.length;
  if (!total) return;
  const rendered = nearestRenderedIndex();
  centeredRendered.value = rendered;
  activeIndex.value = ((rendered % total) + total) % total;
  clearTimeout(scrollIdleTimer);
  scrollIdleTimer = setTimeout(recenterLoop, 120);
}

function onScrollEnd() {
  clearTimeout(scrollIdleTimer);
  recenterLoop();
}

// Once the swipe settles, jump back into the middle copy (no animation) so the
// user can keep swiping the same direction through every card forever.
function recenterLoop() {
  const total = cards.value.length;
  if (total <= 1) return;
  const rendered = nearestRenderedIndex();
  const real = ((rendered % total) + total) % total;
  activeIndex.value = real;
  settledIndex.value = real;
  if (rendered < total || rendered >= total * 2) {
    jumpTo(real + total);
  } else {
    centeredRendered.value = rendered;
  }
}

// Instant, non-animated hop to a rendered card (used for the loop recenter and
// the initial park); the scale swap between clone copies must not animate.
function jumpTo(renderedIndex) {
  suppressTransition.value = true;
  centeredRendered.value = renderedIndex;
  scrollToRendered(renderedIndex, "auto");
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      suppressTransition.value = false;
    });
  });
}

// Park the viewport on the active card within the middle copy.
function centreLoop() {
  jumpTo(activeIndex.value + loopOffset());
}

// Move one card at a time (keyboard + dots).
function step(delta) {
  scrollToRendered(centeredRendered.value + delta);
}

function goTo(index) {
  scrollToRendered(index + loopOffset());
}

// Desktop arrows step the selection one card at a time (wrapping), keeping the
// mobile scroll track in sync so both layouts share the same active card.
function goToReal(index) {
  const total = cards.value.length;
  if (!total) return;
  const real = ((index % total) + total) % total;
  activeIndex.value = real;
  settledIndex.value = real;
  scrollToRendered(real + loopOffset());
}

function prev() {
  goToReal(activeIndex.value - 1);
}

function next() {
  goToReal(activeIndex.value + 1);
}

function onKeydown(event) {
  if (event.key === "ArrowLeft") {
    event.preventDefault();
    step(-1);
  } else if (event.key === "ArrowRight") {
    event.preventDefault();
    step(1);
  }
}

let resizeTimer = null;
function onResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    centreLoop();
    measureFan();
  }, 120);
}

// Size the fan box to the union bounds of the fanned cards so the desktop row
// grows with the card content. Cards are anchored to the fan's bottom, so we
// also lift them (--fan-card-bottom) by however far the lowest card overflows.
function measureFan() {
  const fan = fanRef.value;
  const anchor = fanAnchorRef.value;
  if (!fan || !anchor) return;
  const fanRect = fan.getBoundingClientRect();
  if (!fanRect.width) return; // hidden (mobile layout)
  const cardEls = Array.from(
    fan.querySelectorAll(".ca-card-fan-carousel__fan-card"),
  );
  if (!cardEls.length) return;
  // The anchor marker shares the cards' `bottom` offset, so it tracks their
  // reference line; measuring the up/down extents from it stays consistent even
  // if the sizing runs before the previous update has been laid out.
  const anchorY = anchor.getBoundingClientRect().top;
  let minTop = Infinity;
  let maxBottom = -Infinity;
  let minLeft = Infinity;
  let maxRight = -Infinity;
  cardEls.forEach((el) => {
    const r = el.getBoundingClientRect();
    minTop = Math.min(minTop, r.top);
    maxBottom = Math.max(maxBottom, r.bottom);
    minLeft = Math.min(minLeft, r.left);
    maxRight = Math.max(maxRight, r.right);
  });
  const upExtent = anchorY - minTop;
  const downExtent = maxBottom - anchorY;
  fanCardBottom.value = Math.max(0, Math.round(downExtent));
  fanHeight.value = Math.ceil(upExtent + downExtent);
  // Re-centre the fanned cards in the column: shift the fan so the cards'
  // horizontal centre lines up with the box centre (which the column already
  // centres). Invariant under its own transform, so it settles in one pass.
  const boxCentre = fanRect.left + fanRect.width / 2;
  const contentCentre = (minLeft + maxRight) / 2;
  fanOffsetX.value = Math.round(boxCentre - contentCentre);
}

let entranceObserver = null;
function observeEntrance() {
  if (inView.value) return;
  const root = rootRef.value;
  if (!root) return;
  if (typeof IntersectionObserver === "undefined") {
    inView.value = true; // no observer support: show immediately
    return;
  }
  entranceObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        inView.value = true;
        entranceObserver?.disconnect();
        entranceObserver = null;
      }
    },
    { threshold: 0.25 },
  );
  entranceObserver.observe(root);
}

onMounted(async () => {
  // Only fall back to the mock when the backend sent no data at all.
  if (isDev && !props.cards.length) {
    const { mockCardFanCarousel9 } =
      await import("./mocks/cardFanCarouselMocks.js");
    mock.value = mockCardFanCarousel9;
  }
  window.addEventListener("resize", onResize);
  await nextTick();
  centreLoop();
  measureFan();
  observeEntrance();
});

// Re-park in the middle copy whenever the data changes.
watch(cards, async () => {
  await nextTick();
  centreLoop();
  measureFan();
  observeEntrance();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
  entranceObserver?.disconnect();
  clearTimeout(scrollIdleTimer);
  clearTimeout(resizeTimer);
  clearTimeout(msgSwapTimer);
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Card fan carousel (mobile first).
// A native horizontal scroll-snap track (same swipe as CaOverlappingCarousel):
// the finger drives the scroll, the browser snaps to the centred card and the
// centred card is scaled up while its neighbours sit smaller in the row.
// ---------------------------------------------------------------------------
.ca-card-fan-carousel {
  width: 100%;
  padding-block: var(--size-11);
}

// The mobile scroll track and the desktop fan are two separate layouts driven
// by the same active card; only one is shown per breakpoint.
.ca-card-fan-carousel__desktop {
  display: none;
}

// Mobile-first: below 1366px use the mobile scroll track; the desktop fan only
// kicks in at 1366px+ (narrower desktops clip the fanned-out cards).
@media (min-width: 1366px) {
  .ca-card-fan-carousel__mobile {
    display: none;
  }

  .ca-card-fan-carousel__desktop {
    display: flex;
    align-items: flex-start;
    gap: var(--size-9);
  }

  // Left half: centre the fixed-width fan within its flex track.
  .ca-card-fan-carousel__fan-col {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    justify-content: center;
  }

  // Right half: the navigation + message panel.
  .ca-card-fan-carousel__panel {
    flex: 1 1 0;
    min-width: 0;
  }
}

.ca-card-fan-carousel__inner {
  width: 100%;
  gap: var(--size-5);
}

.ca-card-fan-carousel__stage {
  --fan-card-width: 215px;
  --fan-card-height: 340px;
  --fan-side-scale: 0.82;
  --fan-gap: 16px;
  --fan-ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --fan-duration: 0.35s;

  position: relative;
  // Full-bleed out of the container padding so the neighbour cards reach the
  // page margins; the symmetric padding below keeps the active card centred.
  width: calc(100% + ca-margin("base") * 2);
  margin-inline: calc(ca-margin("base") * -1);
  // Extra vertical room so the card shadow is not clipped by overflow.
  height: calc(var(--fan-card-height) + 64px);
  display: flex;
  align-items: center;
  // Native horizontal scroll with snap; symmetric padding centres the snapped
  // card and reveals its neighbours on each side.
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  overscroll-behavior-inline: contain;
  -webkit-overflow-scrolling: touch;
  padding-inline: calc(50% - var(--fan-card-width) / 2);
  // Reset <ul> defaults (keeps the list markup without changing layout).
  margin-block: 0;
  list-style: none;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid var(--ca-color-outline-focus-only);
    outline-offset: 2px;
    border-radius: var(--size-4);
  }
}

@include ca-bp("sm") {
  .ca-card-fan-carousel__stage {
    width: calc(100% + ca-margin("sm") * 2);
    margin-inline: calc(ca-margin("sm") * -1);
  }
}

@include ca-bp("md") {
  .ca-card-fan-carousel__stage {
    // Keep the base card size (215×340) across the whole mobile range (up to
    // the 1366px desktop switch); only the full-bleed margin changes per bp.
    --fan-gap: 20px;
    width: calc(100% + ca-margin("md") * 2);
    margin-inline: calc(ca-margin("md") * -1);
  }

  .ca-card-fan-carousel__inner {
    gap: var(--size-8);
  }
}

@include ca-bp("lg") {
  .ca-card-fan-carousel__stage {
    width: calc(100% + ca-margin("lg") * 2);
    margin-inline: calc(ca-margin("lg") * -1);
  }
}

@include ca-bp("lg2") {
  .ca-card-fan-carousel__stage {
    width: calc(100% + ca-margin("lg2") * 2);
    margin-inline: calc(ca-margin("lg2") * -1);
  }
}

@include ca-bp("xl") {
  .ca-card-fan-carousel__stage {
    width: calc(100% + ca-margin("xl") * 2);
    margin-inline: calc(ca-margin("xl") * -1);
  }
}

.ca-card-fan-carousel__card {
  position: relative;
  flex-shrink: 0;
  width: var(--fan-card-width);
  height: var(--fan-card-height);
  padding: 0;
  border: none;
  background: transparent;
  border-radius: var(--size-5);
  list-style: none;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  // Neighbours sit smaller; the centred card scales back up to full size. The
  // translateX pulls each card toward the centre to even out the gaps the
  // scale insets would otherwise make uneven (see --gap-steps). One step = the
  // per-side inset of the scale.
  transform: translateX(
      calc(
        var(--gap-steps, 0) * (1 - var(--fan-side-scale)) *
          var(--fan-card-width) / 2
      )
    )
    scale(var(--fan-side-scale));
  transform-origin: 50% 92%;
  transition: transform var(--fan-duration) var(--fan-ease);
  will-change: transform;
}

.ca-card-fan-carousel__card--active {
  transform: translateX(
      calc(
        var(--gap-steps, 0) * (1 - var(--fan-side-scale)) *
          var(--fan-card-width) / 2
      )
    )
    scale(1);
  z-index: 2;
}

// Soften the card drop-shadow (scoped to this carousel) so it doesn't read as a
// grey box-shadow, especially where the active card overlaps its neighbours.
.ca-card-fan-carousel :deep(.base-card) {
  --base-card-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

// Entrance ("open from the centre"): the inner wrapper starts collapsed exactly
// at the centre (hidden behind the opaque active card, which sits on top) and
// slides out to its own place when the carousel enters view. No opacity change
// — it's a pure positional move. The transform lives here so it doesn't fight
// the active/side scale that sits on the card itself.
.ca-card-fan-carousel__card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transform: translateX(
    calc(var(--enter-offset, 0) * var(--fan-card-width, 240px) * -1)
  );
  transition: transform 1.1s var(--fan-ease, ease);
  transition-delay: var(--enter-delay, 0s);
}

.ca-card-fan-carousel.is-in-view .ca-card-fan-carousel__card-inner {
  transform: translateX(0);
}

// While collapsed behind the active card (before the open plays), drop the
// non-active cards' shadow so the stacked shadows don't read as a grey blob.
.ca-card-fan-carousel:not(.is-in-view)
  .ca-card-fan-carousel__card:not(.ca-card-fan-carousel__card--active)
  :deep(.base-card) {
  filter: none;
}

@media (prefers-reduced-motion: reduce) {
  .ca-card-fan-carousel__card-inner {
    transition: none;
  }
}

// During the silent loop recenter, don't animate the scale swap between copies.
.ca-card-fan-carousel__stage--jumping .ca-card-fan-carousel__card {
  transition: none;
}

// Re-tint the dots for a light background (the DS defaults target dark ones).
.ca-card-fan-carousel__dots {
  :deep(.ca-page-control__dot) {
    background-color: var(--ca-color-neutrals-on-background-low-30);
  }

  :deep(.ca-page-control__dot--active) {
    background-color: var(--ca-color-theme-primary, #066542);
  }
}

.ca-card-fan-carousel__message {
  max-width: 480px;
  text-align: center;
  align-items: center;
}

// Mobile: JS-driven out-in swap. The message fades out (is-leaving), the data
// swaps at the mid-point, then it fades back in — a plain opacity/transform
// transition, so it can't stall mid-swipe the way <Transition out-in> does.
// It also starts hidden and fades in on entrance (is-in-view), like desktop.
.ca-card-fan-carousel__message--mobile {
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.ca-card-fan-carousel.is-in-view .ca-card-fan-carousel__message--mobile {
  opacity: 1;
  transform: none;
}

// Swap fade-out; scoped under is-in-view so it outweighs the entrance reveal.
.ca-card-fan-carousel.is-in-view
  .ca-card-fan-carousel__message--mobile.is-leaving {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .ca-card-fan-carousel__message--mobile {
    transition: none;
  }
}

// ---------------------------------------------------------------------------
// Desktop fan (md+): cards fan out from a shared bottom pivot on the left, the
// navigation arrows and the message sit on the right.
// ---------------------------------------------------------------------------
.ca-card-fan-carousel__fan {
  --fan-card-width: 180px;
  --fan-card-height: 280px;
  --fan-ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --fan-duration: 0.45s;
  --fan-open-duration: 0.55s;

  position: relative;
  width: var(--fan-card-width);
  // Height follows the card size so the desktop row grows with the card
  // content instead of a hard-coded value.
  height: var(--fan-card-height);
  // Reset <ul> defaults (keeps the list markup without changing layout).
  margin-block: 0;
  padding-inline-start: 0;
  list-style: none;
  // Re-centre the fanned cards (which open out past this narrow box) inside the
  // column; the offset is measured in JS (see measureFan).
  transform: translateX(var(--fan-offset-x, 0px));
  // Hidden until the component scrolls into view (see --visible); avoids
  // showing the fan before the entrance animation plays.
  opacity: 0;
  transition: opacity 0.4s ease;
}

.ca-card-fan-carousel__fan--visible {
  opacity: 1;
}

// Zero-size marker sharing the cards' bottom offset; used to measure the fan.
.ca-card-fan-carousel__fan-anchor {
  position: absolute;
  left: 0;
  bottom: var(--fan-card-bottom, 0px);
  width: 0;
  height: 0;
}

.ca-card-fan-carousel__fan-card {
  position: absolute;
  left: 0;
  bottom: var(--fan-card-bottom, 0px);
  width: var(--fan-card-width);
  height: var(--fan-card-height);
  cursor: pointer;
  list-style: none;
  --card-tx: calc(var(--card-ox, 0) * var(--fan-card-width));
  --card-ty: calc(var(--card-oy, 0) * var(--fan-card-width));
  // Per-card placement measured from the design: offset from the front card
  // (in card-width units, so it scales with the breakpoint), rotation and scale.
  transform: translate(var(--card-tx), var(--card-ty))
    rotate(var(--card-rot, 0deg)) scale(var(--card-scale, 1));
  transform-origin: 50% 50%;
  transition: transform var(--fan-duration) var(--fan-ease);
  will-change: transform;

  &:focus-visible {
    outline: none;
  }

  &:focus-visible .ca-card-fan-carousel__fan-card-inner {
    outline: 2px solid var(--ca-color-outline-focus-only);
    outline-offset: 2px;
  }
}

// Entrance: the cards "deal out" from the stacked front card to their places,
// staggered front → back.
.ca-card-fan-carousel__fan--animate .ca-card-fan-carousel__fan-card {
  animation: ca-fan-deal var(--fan-open-duration) var(--fan-ease) both;
  animation-delay: var(--card-delay, 0ms);
}

@keyframes ca-fan-deal {
  from {
    transform: translate(0, 0) rotate(8deg) scale(var(--card-scale, 1));
  }
  to {
    transform: translate(var(--card-tx), var(--card-ty))
      rotate(var(--card-rot, 0deg)) scale(var(--card-scale, 1));
  }
}

// The selected card scales about its own centre (grows in place); its position,
// rotation and stacking stay unchanged. A stable compositing layer avoids the
// light-edge fringe Chrome renders when scaling an element that owns a filter
// (the card's drop-shadow).
.ca-card-fan-carousel__fan-card-inner {
  width: 100%;
  height: 100%;
  transform-origin: center center;
  transition: transform var(--fan-duration) var(--fan-ease);
  will-change: transform;
  backface-visibility: hidden;
}

.ca-card-fan-carousel__fan-card--active .ca-card-fan-carousel__fan-card-inner {
  transform: scale(1.08333);
}

.ca-card-fan-carousel__panel {
  gap: var(--size-8);
}

.ca-card-fan-carousel__arrows {
  gap: var(--size-4);
}

.ca-card-fan-carousel__panel .ca-card-fan-carousel__message {
  text-align: left;
  align-items: flex-start;
  max-width: none;
}

.ca-card-fan-carousel__panel-content {
  align-items: flex-start;
  max-width: 480px;
}

// Entrance fade for the desktop message column — mirrors the swap fade (opacity
// + rise) and plays once the component scrolls into view. The mobile block is
// excluded so its cards slide out from the centre instead of fading in.
.ca-card-fan-carousel__panel {
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 0.9s ease,
    transform 0.9s ease;
  transition-delay: 0.2s;
}

.ca-card-fan-carousel.is-in-view .ca-card-fan-carousel__panel {
  opacity: 1;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .ca-card-fan-carousel__panel {
    transform: none;
  }

  .ca-card-fan-carousel__fan--animate .ca-card-fan-carousel__fan-card {
    animation: none;
  }
}

// A light fade-in as the message swaps to the newly selected card.
.ca-card-fan-carousel-fade-enter-active,
.ca-card-fan-carousel-fade-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}

.ca-card-fan-carousel-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.ca-card-fan-carousel-fade-leave-to {
  opacity: 0;
}

.ca-card-fan-carousel__message :deep(.module-message__label) {
  align-self: center;
}

// Left-align the label only in the desktop fan layout (1260px+).
@media (min-width: 1366px) {
  .ca-card-fan-carousel__message :deep(.module-message__label) {
    align-self: flex-start;
  }
}
</style>
